import { useRef, useState } from 'react'
import {
  AlertTriangle, ArrowRightLeft, Camera, CheckCircle, ClipboardCheck, FileText, Gavel, History, Loader2,
  MapPin, Printer, Trash2, Upload, X, XCircle,
} from 'lucide-react'
import { toast } from 'sonner'
import Modal from '@/components/common/Modal'
import InlineField from '@/components/common/InlineField'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { equipment as equipmentApi } from '@/utils/api'
import { useEquipmentAction, useEquipmentDetail, useEquipmentMeta, useInvalidateEquipment } from '@/hooks/useEquipment'
import type {
  DefectDecision, Equipment, EquipmentDefect, EquipmentDetail, EquipmentPhoto, PhotoKind,
} from '@/types/equipment'
import { LocationCell, StatusBadge, WarrantyBadge } from './EquipmentBadges'
import {
  CONDITION_CONFIG, CONDITION_KEYS, DECISION_CONFIG, DECISION_KEYS, HISTORY_CONFIG,
  daysText, formatDateTime, formatEuro, formatIsoDate,
} from './equipmentUtils'
import MoveDialog from './MoveDialog'
import LabelDialog from './LabelDialog'

type Tab = 'stammdaten' | 'defekte' | 'verlauf'

const PHOTO_KIND_LABEL: Record<PhotoKind, string> = {
  geraet: 'Gerät', typenschild: 'Typenschild', defekt: 'Defekt', beleg: 'Beleg',
}

interface Props {
  equipmentId: number | null
  onClose: () => void
}

export default function GeraetDetailModal({ equipmentId, onClose }: Props) {
  const { data, isLoading } = useEquipmentDetail(equipmentId)
  const [tab, setTab] = useState<Tab>('stammdaten')
  const [moveOpen, setMoveOpen] = useState(false)
  const [labelOpen, setLabelOpen] = useState(false)
  const [defectOpen, setDefectOpen] = useState(false)
  const [lightbox, setLightbox] = useState<EquipmentPhoto | null>(null)

  const item = data?.equipment
  const isRetired = item?.status === 'ausgemustert'

  return (
    <>
      <Modal
        isOpen={equipmentId != null}
        onClose={onClose}
        size="screen"
        className="flex flex-col"
        bodyClassName="grid min-h-0 flex-1 grid-cols-1 overflow-hidden lg:grid-cols-[minmax(320px,38%)_1fr]"
        headerContent={
          <div className="flex h-16 flex-shrink-0 items-center gap-3 border-b bg-white px-4">
            {item ? (
              <>
                <span className="rounded-md bg-primary-50 px-2 py-1 font-mono text-sm font-bold text-primary-700">{item.unique_code}</span>
                <h2 className="min-w-0 truncate text-lg font-semibold">{item.name}</h2>
                <StatusBadge status={item.status} />
                <div className="ml-auto flex items-center gap-2">
                  {!isRetired && item.status !== 'aussonderung_beantragt' && (
                    <Button size="sm" variant="outline" onClick={() => setMoveOpen(true)}><ArrowRightLeft className="h-4 w-4" /> Umbuchen</Button>
                  )}
                  {!isRetired && !item.open_defect_id && (
                    <Button size="sm" variant="outline" onClick={() => setDefectOpen(true)}><AlertTriangle className="h-4 w-4" /> Defekt melden</Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setLabelOpen(true)}><Printer className="h-4 w-4" /> Etikett</Button>
                </div>
              </>
            ) : <Skeleton className="h-6 w-64" />}
            <button onClick={onClose} className="ml-2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Schließen">
              <X className="h-5 w-5" />
            </button>
          </div>
        }
      >
        {isLoading || !data || !item ? (
          <div className="col-span-full space-y-3 p-6"><Skeleton className="h-40 w-full" /><Skeleton className="h-64 w-full" /></div>
        ) : (
          <>
            <PhotoPane item={item} photos={data.photos} onOpen={setLightbox} />
            <div className="flex min-h-0 flex-col overflow-hidden">
              <SummaryBar item={item} />
              <div className="flex gap-1 border-b px-4">
                {([
                  ['stammdaten', 'Stammdaten & Garantie'],
                  ['defekte', `Defekte${data.defects.length ? ` (${data.defects.length})` : ''}`],
                  ['verlauf', 'Verlauf'],
                ] as [Tab, string][]).map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setTab(key)}
                    className={cn(
                      '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                      tab === key ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-800',
                    )}
                  >
                    {label}
                    {key === 'defekte' && data.openDefect && <span className="ml-1.5 inline-block h-2 w-2 rounded-full bg-red-500" />}
                  </button>
                ))}
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-4">
                {tab === 'stammdaten' && <MasterDataTab item={item} />}
                {tab === 'defekte' && <DefectsTab defects={data.defects} photos={data.photos} onPhoto={setLightbox} />}
                {tab === 'verlauf' && <HistoryTab history={data.history} />}
              </div>
            </div>
          </>
        )}
      </Modal>

      {/* Dialoge NACH dem Modal rendern — gleicher z-Index, DOM-Reihenfolge entscheidet */}
      <MoveDialog item={moveOpen ? item ?? null : null} onClose={() => setMoveOpen(false)} />
      {item && <LabelDialog items={[item]} open={labelOpen} onClose={() => setLabelOpen(false)} />}
      {item && <ReportDefectDialog item={item} open={defectOpen} onClose={() => { setDefectOpen(false); setTab('defekte') }} />}
      {lightbox && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-6" onClick={() => setLightbox(null)}>
          <img src={lightbox.file_path} alt="" className="max-h-full max-w-full rounded shadow-2xl" />
        </div>
      )}
    </>
  )
}

// ==================== Linke Spalte: Fotos & Belege ====================

function PhotoPane({ item, photos, onOpen }: { item: Equipment; photos: EquipmentPhoto[]; onOpen: (p: EquipmentPhoto) => void }) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [kind, setKind] = useState<PhotoKind>('geraet')
  const [uploading, setUploading] = useState(false)
  const invalidate = useInvalidateEquipment()

  const upload = async (files: FileList | null) => {
    if (!files?.length) return
    setUploading(true)
    try {
      await equipmentApi.uploadFiles(item.id, Array.from(files), kind)
      toast.success(files.length > 1 ? `${files.length} Dateien hochgeladen` : 'Datei hochgeladen')
      invalidate()
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Upload fehlgeschlagen')
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const remove = async (photo: EquipmentPhoto) => {
    if (!window.confirm('Datei wirklich löschen?')) return
    try {
      await equipmentApi.deletePhoto(photo.id)
      invalidate()
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Löschen fehlgeschlagen')
    }
  }

  const images = photos.filter((p) => p.kind !== 'beleg' && !(p.mime_type || '').includes('pdf'))
  const docs = photos.filter((p) => p.kind === 'beleg' || (p.mime_type || '').includes('pdf'))

  return (
    <div className="flex min-h-0 flex-col overflow-y-auto border-r bg-gray-50 p-4">
      <div className="mb-3 flex items-center gap-2">
        <select value={kind} onChange={(e) => setKind(e.target.value as PhotoKind)} className="h-9 rounded-md border bg-white px-2 text-sm">
          {(Object.keys(PHOTO_KIND_LABEL) as PhotoKind[]).map((k) => <option key={k} value={k}>{PHOTO_KIND_LABEL[k]}</option>)}
        </select>
        <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading}>
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Hochladen
        </Button>
        <input ref={inputRef} type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={(e) => upload(e.target.files)} />
      </div>

      {images.length === 0 ? (
        <button
          onClick={() => inputRef.current?.click()}
          className="flex aspect-[4/3] w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 text-gray-400 hover:border-primary-400 hover:text-primary-600"
        >
          <Camera className="h-8 w-8" />
          <span className="text-sm">Foto vom Gerät / Typenschild hinzufügen</span>
        </button>
      ) : (
        <>
          <button onClick={() => onOpen(images[0])} className="mb-2 overflow-hidden rounded-lg border bg-white">
            <img src={images[0].file_path} alt={item.name} className="aspect-[4/3] w-full object-contain" />
          </button>
          <div className="grid grid-cols-4 gap-2">
            {images.map((p) => (
              <div key={p.id} className="group relative">
                <button onClick={() => onOpen(p)} className="block w-full overflow-hidden rounded border bg-white">
                  <img src={p.thumb_path || p.file_path} alt="" className="aspect-square w-full object-cover" />
                </button>
                <span className="absolute bottom-0.5 left-0.5 rounded bg-black/60 px-1 text-[10px] text-white">{PHOTO_KIND_LABEL[p.kind]}</span>
                <button onClick={() => remove(p)} className="absolute right-0.5 top-0.5 hidden rounded bg-white/90 p-0.5 text-red-600 group-hover:block" aria-label="Löschen">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="mt-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Belege</p>
        {docs.length === 0 ? (
          <p className="text-sm text-gray-400">Kein Kaufbeleg hinterlegt — für Garantiefälle Rechnung als PDF hochladen (Typ „Beleg").</p>
        ) : (
          <ul className="space-y-1">
            {docs.map((d) => (
              <li key={d.id} className="group flex items-center gap-2 rounded border bg-white px-2 py-1.5 text-sm">
                <FileText className="h-4 w-4 shrink-0 text-gray-400" />
                <a href={d.file_path} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate text-primary-700 hover:underline">
                  {d.original_name || 'Beleg'}
                </a>
                <span className="text-xs text-gray-400">{formatIsoDate(d.created_at)}</span>
                <button onClick={() => remove(d)} className="hidden text-red-600 group-hover:block" aria-label="Löschen"><Trash2 className="h-3.5 w-3.5" /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

// ==================== Kopfzeile rechts: Standort / Zustand / Garantie / DGUV ====================

function SummaryBar({ item }: { item: Equipment }) {
  const setCondition = useEquipmentAction(
    ({ condition }: { condition: string }) => equipmentApi.setCondition(item.id, condition),
    'Zustand gespeichert',
  )
  const mapsUrl = item.last_seen_lat != null && item.last_seen_lng != null
    ? `https://www.google.com/maps?q=${item.last_seen_lat},${item.last_seen_lng}` : null

  return (
    <div className="grid grid-cols-2 gap-3 border-b bg-white p-4 xl:grid-cols-4">
      <div>
        <p className="text-[11px] uppercase tracking-wide text-gray-400">Standort</p>
        <LocationCell item={item} />
        {item.location_since && <p className="text-xs text-gray-400">seit {formatIsoDate(item.location_since)}</p>}
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wide text-gray-400">Zustand</p>
        <select
          value={item.condition ?? ''}
          disabled={item.status === 'ausgemustert' || setCondition.isPending}
          onChange={(e) => setCondition.mutate({ condition: e.target.value })}
          className="-ml-1 rounded border-transparent bg-transparent px-1 py-0.5 text-sm hover:border-gray-200"
        >
          {CONDITION_KEYS.map((c) => <option key={c} value={c}>{CONDITION_CONFIG[c].label}</option>)}
        </select>
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wide text-gray-400">Gewährleistung</p>
        <WarrantyBadge state={item.warranty_state} until={item.warranty_effective_until} />
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wide text-gray-400">Zuletzt gesehen</p>
        {item.last_seen_at ? (
          <p className="text-sm">
            {formatDateTime(item.last_seen_at)}
            <span className="text-gray-500"> · {item.last_seen_by_name}</span>
            {mapsUrl && (
              <a href={mapsUrl} target="_blank" rel="noreferrer" className="ml-1 inline-flex text-primary-600" title="Position auf Karte">
                <MapPin className="h-3.5 w-3.5" />
              </a>
            )}
          </p>
        ) : <p className="text-sm text-gray-400">noch nie gescannt</p>}
      </div>
    </div>
  )
}

// ==================== Tab: Stammdaten & Garantie ====================

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-lg border bg-white">
      <p className="border-b bg-gray-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</p>
      {children}
    </div>
  )
}

function MasterDataTab({ item }: { item: Equipment }) {
  const { data: meta } = useEquipmentMeta()
  const invalidate = useInvalidateEquipment()
  const readOnly = item.status === 'ausgemustert'

  const save = async (field: string, value: string | number | null) => {
    try {
      await equipmentApi.update(item.id, { [field]: value })
      invalidate()
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Speichern fehlgeschlagen')
      throw err
    }
  }

  const dguv = useEquipmentAction(
    () => equipmentApi.dguvCheck(item.id, {}),
    (d: Equipment) => `DGUV-Prüfung erfasst, nächste am ${formatIsoDate(d.dguv_next_check)}`,
  )
  const decommission = useEquipmentAction(() => equipmentApi.decommission(item.id, 'In der WebUI ausgemustert'), 'Gerät ausgemustert')

  const f = (label: string, field: keyof Equipment, extra: Partial<React.ComponentProps<typeof InlineField>> = {}) => (
    <InlineField label={label} field={field} value={item[field] as any} onSave={save} readOnly={readOnly} labelWidth={130} {...extra} />
  )
  const euro = (v: any) => formatEuro(Number(v))

  return (
    <div className="grid gap-4 xl:grid-cols-2">
      <Section title="Gerät">
        {f('Name', 'name')}
        {f('Kategorie', 'category', { type: 'select', options: meta?.categories.map((c) => c.name) ?? [] })}
        {f('Hersteller', 'manufacturer')}
        {f('Modell', 'model')}
        {f('Seriennummer', 'serial_number', { mono: true, copyValue: item.serial_number })}
        {f('Notizen', 'notes', { type: 'textarea' })}
      </Section>

      <Section title="Kauf & Gewährleistung">
        {f('Kaufdatum', 'purchase_date', { type: 'date', display: formatIsoDate })}
        {f('Kaufpreis', 'purchase_price', { type: 'currency', display: euro })}
        {f('Händler', 'supplier')}
        {f('Rechnungsnr.', 'invoice_number', { mono: true, copyValue: item.invoice_number })}
        {f('Garantie bis', 'warranty_until', { type: 'date', display: formatIsoDate, placeholder: 'leer = 24 Monate ab Kauf' })}
        {f('Garantie-Notiz', 'warranty_note', { placeholder: 'z.B. Hilti Lifetime Service, 3 Jahre nach Registrierung' })}
        {f('Nutzungsdauer (J.)', 'useful_life_years', {
          type: 'number',
          placeholder: `Standard ${meta?.categories.find((c) => c.name === item.category)?.usefulLifeYears ?? 6} Jahre`,
        })}
      </Section>

      <Section title="DGUV V3 Prüfung">
        {f('Intervall (Mon.)', 'dguv_interval_months', { type: 'number' })}
        {f('Letzte Prüfung', 'dguv_last_check', { type: 'date', display: formatIsoDate })}
        {f('Nächste Prüfung', 'dguv_next_check', {
          type: 'date',
          display: (v: string) => (
            <span className={cn(item.dguv_days_left != null && item.dguv_days_left < 0 && 'font-semibold text-red-600')}>
              {formatIsoDate(v)} <span className="text-xs text-gray-400">{daysText(item.dguv_days_left)}</span>
            </span>
          ),
        })}
        {!readOnly && (
          <div className="border-t px-3 py-2">
            <Button size="sm" variant="outline" onClick={() => dguv.mutate(undefined)} disabled={dguv.isPending}>
              <ClipboardCheck className="h-4 w-4" /> Heute geprüft
            </Button>
          </div>
        )}
      </Section>

      {!readOnly && (
        <Section title="Aussondern">
          <div className="space-y-2 p-3 text-sm text-gray-600">
            <p>Direkt ausmustern (z.B. verloren oder gestohlen). Bei Defekten besser den Defekt-Ablauf nutzen — dann bleibt die Entscheidung dokumentiert.</p>
            <Button
              size="sm"
              variant="outline"
              className="text-rose-700"
              disabled={decommission.isPending}
              onClick={() => window.confirm(`${item.name} (${item.unique_code}) endgültig ausmustern?`) && decommission.mutate(undefined)}
            >
              <Trash2 className="h-4 w-4" /> Ausmustern
            </Button>
          </div>
        </Section>
      )}
    </div>
  )
}

// ==================== Tab: Defekte ====================

export function DefectCard({ defect, photos = [], onPhoto, compact }: {
  defect: EquipmentDefect
  photos?: EquipmentPhoto[]
  onPhoto?: (p: EquipmentPhoto) => void
  compact?: boolean
}) {
  const [closeOpen, setCloseOpen] = useState(false)
  const decide = useEquipmentAction(
    ({ decision }: { decision: DefectDecision }) => equipmentApi.decide(defect.id, decision),
    'Entscheidung gespeichert',
  )
  const confirm = useEquipmentAction(() => equipmentApi.confirmDisposal(defect.id), 'Aussonderung bestätigt — Gerät ausgemustert')
  const reject = useEquipmentAction(
    ({ note }: { note: string }) => equipmentApi.rejectDisposal(defect.id, note),
    'Aussonderung abgelehnt — Entscheidung wieder offen',
  )

  const isOpen = defect.status === 'offen' || defect.status === 'entschieden'
  const disposalPending = defect.status === 'entschieden' && defect.decision === 'entsorgen'
  const rec = defect.recommendation ? DECISION_CONFIG[defect.recommendation] : null
  const defectPhotos = photos.filter((p) => p.defect_id === defect.id)

  return (
    <div className={cn('rounded-lg border bg-white', isOpen ? 'border-red-200' : 'opacity-80')}>
      <div className="flex flex-wrap items-center gap-2 border-b px-3 py-2">
        <AlertTriangle className={cn('h-4 w-4', isOpen ? 'text-red-500' : 'text-gray-400')} />
        <span className="text-sm font-medium">Defekt vom {formatDateTime(defect.reported_at)}</span>
        <span className="text-sm text-gray-500">· {defect.reported_by_name || '–'}</span>
        <span className="ml-auto">
          {defect.decision ? (
            <span className={cn('rounded-md border px-2 py-0.5 text-xs font-medium', DECISION_CONFIG[defect.decision].className)}>
              {DECISION_CONFIG[defect.decision].short}
            </span>
          ) : <span className="rounded-md border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">Entscheidung offen</span>}
          {defect.status === 'erledigt' && <span className="ml-1 rounded-md border bg-gray-50 px-2 py-0.5 text-xs text-gray-600">Erledigt</span>}
        </span>
      </div>
      <div className="space-y-3 p-3">
        {defect.description && <p className="whitespace-pre-wrap text-sm">{defect.description}</p>}
        {defectPhotos.length > 0 && (
          <div className="flex gap-2">
            {defectPhotos.map((p) => (
              <button key={p.id} onClick={() => onPhoto?.(p)} className="overflow-hidden rounded border">
                <img src={p.thumb_path || p.file_path} alt="" className="h-16 w-16 object-cover" />
              </button>
            ))}
          </div>
        )}
        {rec && (
          <div className={cn('rounded-md border px-3 py-2 text-sm', rec.className)}>
            <p className="font-medium">Empfehlung: {rec.label}</p>
            <p className="text-xs opacity-90">{defect.recommendation_reason}</p>
          </div>
        )}

        {defect.status === 'offen' && (
          <div className="flex flex-wrap gap-2">
            {DECISION_KEYS.map((d) => (
              <Button
                key={d}
                size="sm"
                variant={defect.recommendation === d ? 'default' : 'outline'}
                disabled={decide.isPending}
                onClick={() => decide.mutate({ decision: d })}
                title={DECISION_CONFIG[d].hint}
              >
                <Gavel className="h-4 w-4" /> {DECISION_CONFIG[d].label}
              </Button>
            ))}
          </div>
        )}

        {disposalPending && (
          <div className="rounded-md border border-rose-200 bg-rose-50 p-3">
            <p className="mb-2 text-sm text-rose-800">
              <strong>{defect.decided_by_name}</strong> möchte das Gerät entsorgen ({formatDateTime(defect.decided_at)}). Bestätigen?
            </p>
            <div className="flex gap-2">
              <Button size="sm" className="bg-rose-600 hover:bg-rose-700" disabled={confirm.isPending} onClick={() => confirm.mutate(undefined)}>
                <CheckCircle className="h-4 w-4" /> Aussonderung bestätigen
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={reject.isPending}
                onClick={() => {
                  const note = window.prompt('Warum nicht entsorgen? (optional)') ?? null
                  if (note !== null) reject.mutate({ note: note || 'Aussonderung abgelehnt' })
                }}
              >
                <XCircle className="h-4 w-4" /> Ablehnen
              </Button>
            </div>
          </div>
        )}

        {defect.status === 'entschieden' && defect.decision !== 'entsorgen' && (
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-xs text-gray-500">
              Entschieden von {defect.decided_by_name} am {formatDateTime(defect.decided_at)}. {defect.decision && DECISION_CONFIG[defect.decision].hint}
            </p>
            <Button size="sm" onClick={() => setCloseOpen(true)}><CheckCircle className="h-4 w-4" /> {defect.decision === 'garantie' ? 'Aus Garantie zurück' : 'Reparatur abgeschlossen'}</Button>
            {!compact && (
              <select
                value=""
                onChange={(e) => e.target.value && decide.mutate({ decision: e.target.value as DefectDecision })}
                className="h-8 rounded-md border px-2 text-xs text-gray-600"
              >
                <option value="">Umentscheiden…</option>
                {DECISION_KEYS.filter((d) => d !== defect.decision).map((d) => <option key={d} value={d}>{DECISION_CONFIG[d].label}</option>)}
              </select>
            )}
          </div>
        )}

        {defect.status === 'erledigt' && (
          <p className="text-xs text-gray-500">
            Abgeschlossen am {formatDateTime(defect.closed_at)}
            {defect.confirmed_by_name && ` · bestätigt von ${defect.confirmed_by_name}`}
            {defect.cost != null && ` · Kosten ${formatEuro(defect.cost)}`}
            {defect.resolution_note && ` · ${defect.resolution_note}`}
          </p>
        )}
      </div>
      <CloseDefectDialog defect={closeOpen ? defect : null} onClose={() => setCloseOpen(false)} />
    </div>
  )
}

function DefectsTab({ defects, photos, onPhoto }: { defects: EquipmentDefect[]; photos: EquipmentPhoto[]; onPhoto: (p: EquipmentPhoto) => void }) {
  if (!defects.length) return <p className="py-8 text-center text-sm text-gray-400">Keine Defekte gemeldet.</p>
  return <div className="space-y-3">{defects.map((d) => <DefectCard key={d.id} defect={d} photos={photos} onPhoto={onPhoto} />)}</div>
}

function CloseDefectDialog({ defect, onClose }: { defect: EquipmentDefect | null; onClose: () => void }) {
  const [cost, setCost] = useState('')
  const [note, setNote] = useState('')
  const [condition, setCondition] = useState('gut')
  const close = useEquipmentAction(
    () => equipmentApi.closeDefect(defect!.id, { cost: cost || null, note: note || null, condition }),
    'Defekt abgeschlossen — Gerät wieder einsatzbereit',
  )
  return (
    <Dialog open={!!defect} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Defekt abschließen</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <label className="block space-y-1">
            <span>Kosten (€, optional)</span>
            <input value={cost} onChange={(e) => setCost(e.target.value)} inputMode="decimal" placeholder="z.B. 45,50" className="flex h-9 w-full rounded-md border px-3" />
          </label>
          <label className="block space-y-1">
            <span>Zustand danach</span>
            <select value={condition} onChange={(e) => setCondition(e.target.value)} className="flex h-9 w-full rounded-md border px-2">
              {CONDITION_KEYS.filter((c) => c !== 'defekt').map((c) => <option key={c} value={c}>{CONDITION_CONFIG[c].label}</option>)}
            </select>
          </label>
          <label className="block space-y-1">
            <span>Notiz</span>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="z.B. Kohlebürsten getauscht / Austauschgerät erhalten" className="flex h-9 w-full rounded-md border px-3" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button disabled={close.isPending} onClick={() => close.mutate(undefined, { onSuccess: onClose })}>
            {close.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Abschließen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ReportDefectDialog({ item, open, onClose }: { item: Equipment; open: boolean; onClose: () => void }) {
  const [description, setDescription] = useState('')
  const report = useEquipmentAction(
    () => equipmentApi.reportDefect(item.id, description),
    (d: any) => `Defekt gemeldet — Empfehlung: ${DECISION_CONFIG[d.recommendation.recommendation as DefectDecision].label}`,
  )
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Defekt melden: {item.name}</DialogTitle></DialogHeader>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          placeholder="Was ist kaputt? z.B. Motor läuft nicht an, Kabel angeschmort…"
          className="w-full rounded-md border px-3 py-2 text-sm"
        />
        <p className="text-xs text-muted-foreground">Fotos vom Schaden danach links mit Typ „Defekt" hochladen.</p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button
            disabled={!description.trim() || report.isPending}
            onClick={() => report.mutate(undefined, { onSuccess: () => { setDescription(''); onClose() } })}
          >
            {report.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Melden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ==================== Tab: Verlauf ====================

function HistoryTab({ history }: { history: EquipmentDetail['history'] }) {
  if (!history.length) return <p className="py-8 text-center text-sm text-gray-400">Noch keine Einträge.</p>
  return (
    <ol className="relative space-y-4 border-l border-gray-200 pl-5">
      {history.map((h) => {
        const cfg = HISTORY_CONFIG[h.action] ?? { icon: History, label: h.action, color: 'text-gray-500' }
        const Icon = cfg.icon
        const route = h.action === 'bewegt' && (h.from_label || h.to_label) ? `${h.from_label || '–'} → ${h.to_label || '–'}` : null
        return (
          <li key={h.id} className="relative">
            <span className="absolute -left-[29px] flex h-6 w-6 items-center justify-center rounded-full border bg-white">
              <Icon className={cn('h-3.5 w-3.5', cfg.color)} />
            </span>
            <p className="text-sm">
              <span className="font-medium">{cfg.label}</span>
              {route && <span className="text-gray-600"> · {route}</span>}
              {h.action === 'gescannt' && h.to_label && <span className="text-gray-600"> · {h.to_label}</span>}
            </p>
            {h.note && <p className="text-sm text-gray-600">{h.note}</p>}
            <p className="text-xs text-gray-400">
              {formatDateTime(h.created_at)} · {h.performed_by_name || '–'}
              {h.lat != null && h.lng != null && (
                <a href={`https://www.google.com/maps?q=${h.lat},${h.lng}`} target="_blank" rel="noreferrer" className="ml-1 inline-flex align-middle text-primary-600" title="Position">
                  <MapPin className="h-3 w-3" />
                </a>
              )}
            </p>
          </li>
        )
      })}
    </ol>
  )
}
